/** Data hooks for Phase 2: products, quotes, company profile, dashboard and developer settings. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, qs, tokenStore } from "./client";
import type {
  ApiKey, CompanyProfile, Dashboard, Page, Product, PublicQuote, Quote, QuoteSummary, Webhook, WebhookDelivery,
} from "./types";

type Body = Record<string, unknown>;

function useInvalidate() {
  const qc = useQueryClient();
  return (...prefixes: string[]) => prefixes.forEach((p) => void qc.invalidateQueries({ queryKey: [p] }));
}

export const useProducts = (q: string, active?: boolean) =>
  useQuery({ queryKey: ["products", q, active], queryFn: () => api<Page<Product>>("GET", `/products${qs({ q, active, limit: 100 })}`) });

export function useSaveProduct(id?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Body) => (id ? api<Product>("PATCH", `/products/${id}`, data) : api<Product>("POST", "/products", data)),
    onSuccess: () => invalidate("products", "picker"),
  });
}

export const useQuotes = (filters: { status?: string; deal_id?: string; q?: string; approval?: string }, enabled = true) =>
  useQuery({ queryKey: ["quotes", filters], queryFn: () => api<Page<QuoteSummary>>("GET", `/quotes${qs({ ...filters, limit: 100 })}`), enabled });

export const useQuote = (id: string | undefined) =>
  useQuery({
    queryKey: ["quote", id], queryFn: () => api<Quote>("GET", `/quotes/${id}`), enabled: !!id,
    // Emails send in the background; keep checking until none is still queued.
    refetchInterval: (query) => (query.state.data?.emails.some((e) => e.status === "queued") ? 2000 : false),
  });

export function useQuoteAction() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ path, method = "POST", body }: { path: string; method?: "POST" | "PATCH"; body?: Body }) =>
      api<Quote>(method, path, body),
    onSuccess: (q) => {
      qc.setQueryData(["quote", q.id], q);
      invalidate("quotes", "deal", "deals", "board", "timeline", "dashboard");
    },
  });
}

export const usePublicQuote = (token: string) =>
  useQuery({ queryKey: ["public-quote", token], queryFn: () => api<PublicQuote>("GET", `/public/quotes/${token}`), retry: false });

export function usePublicDecision(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { decision: "accepted" | "rejected"; name: string; comment?: string }) =>
      api<PublicQuote>("POST", `/public/quotes/${token}/decision`, body),
    onSuccess: (q) => qc.setQueryData(["public-quote", token], q),
  });
}

export const useCompany = () => useQuery({ queryKey: ["company"], queryFn: () => api<CompanyProfile>("GET", "/company"), staleTime: 60_000 });

export function useSaveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Body) => api<CompanyProfile>("PATCH", "/company", data),
    onSuccess: (c) => {
      qc.setQueryData(["company"], c);
      void qc.invalidateQueries({ queryKey: ["me"] });
      void qc.invalidateQueries({ queryKey: ["ai-status"] });
    },
  });
}

export const useDashboard = () =>
  useQuery({ queryKey: ["dashboard"], queryFn: () => api<Dashboard>("GET", "/dashboard"), refetchInterval: 60_000 });

export const useApiKeys = () => useQuery({ queryKey: ["api-keys"], queryFn: () => api<ApiKey[]>("GET", "/api-keys") });
export const useWebhooks = () => useQuery({ queryKey: ["webhooks"], queryFn: () => api<Webhook[]>("GET", "/webhooks") });
export const useWebhookEvents = () => useQuery({ queryKey: ["webhook-events"], queryFn: () => api<string[]>("GET", "/webhooks/events"), staleTime: Infinity });
export const useDeliveries = (id: string | null) =>
  useQuery({
    queryKey: ["deliveries", id], queryFn: () => api<WebhookDelivery[]>("GET", `/webhooks/${id}/deliveries`),
    enabled: !!id, refetchInterval: 2000,
  });

export function useDeveloperAction<T>() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ method, path, body }: { method: "POST" | "PUT" | "DELETE"; path: string; body?: Body }) => api<T>(method, path, body),
    onSuccess: () => invalidate("api-keys", "webhooks", "deliveries"),
  });
}

/** Fetch an authenticated PDF and open it in a new tab. */
export async function openPdf(path: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { headers: { Authorization: `Bearer ${tokenStore.get() ?? ""}` } });
  if (!res.ok) throw new Error(`Could not load the PDF (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
